<script setup>
import { inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useUserStore } from "@/stores/user";
import Logo from "@/layout/Logo.vue";
import NeedHelpSideDrawer from "@/components/organisms/NeedHelpSideDrawer.vue";
import { getFrequentlyAskedQuestions } from "@/api/metadataApi";
import HelpIcon from "@/components/atoms/icons/HelpIcon.vue";
import { useResizeObserver } from "@vueuse/core";

const userStore = useUserStore();
const isLessXl = inject('isLessXl', ref(false));

const helpSideDrawerRef = ref(null);
const dataForHelpSideDrawer = ref(null);
const headerRef = ref(null);
const mobileHeaderOffsetKey = '--mobile-header-offset';

const setMobileHeaderOffset = () => {
  if (!isLessXl?.value || !headerRef.value) {
    return;
  }

  const { height } = headerRef.value.getBoundingClientRect();
  document.documentElement.style.setProperty(mobileHeaderOffsetKey, `${height}px`);
};

const clearMobileHeaderOffset = () => {
  document.documentElement.style.removeProperty(mobileHeaderOffsetKey);
};

const openHelpSideDrawer = async () => {
  dataForHelpSideDrawer.value = await getFrequentlyAskedQuestions();

  await nextTick();

  if (helpSideDrawerRef.value && typeof helpSideDrawerRef.value.openHelpDrawer === 'function') {
    helpSideDrawerRef.value.openHelpDrawer();
  }
};

useResizeObserver(headerRef, () => setMobileHeaderOffset());

onMounted(() => {
  nextTick(() => setMobileHeaderOffset());
});

watch(isLessXl, (value) => {
  if (value) {
    nextTick(() => setMobileHeaderOffset());
    return;
  }

  clearMobileHeaderOffset();
});

onBeforeUnmount(() => {
  clearMobileHeaderOffset();
});
</script>

<template>
  <NeedHelpSideDrawer 
    ref="helpSideDrawerRef" 
    :phone="user.data.phone"
    :dataForHelpSideDrawer="dataForHelpSideDrawer || {}"
  />

  <header
      ref="headerRef"
      class="bg-white"
      :class="{ 'header--sticky-mobile': isLessXl }"
  >
    <div class="container custom-width">
      <div class="d-flex align-items-center justify-content-between custom-margin">
        <a href="/" class="header__logo-link">
          <Logo v-if="!userStore.isPanelIFrame()"/>
        </a>

        <div class="d-flex flex-column gap-1 flex-shrink-0">

          <button
              v-if="userStore.isIm()"
              class="help-btn d-flex align-items-center"
              @click="openHelpSideDrawer()"
          >
            <HelpIcon class="help-btn__icon" />
            <span class="help-btn__label d-none d-sm-inline">Χρειάζεσαι Βοήθεια;</span>
            <span class="help-btn__label d-inline d-sm-none">Βοήθεια</span>
          </button>
        </div>

      </div>
    </div>
  </header>
</template>

<style lang="scss" scoped>
  /* Only the logo shrinks on narrow screens; the help button keeps its content width */
  .header__logo-link {
    min-width: 0;
  }

  .header__logo-link :deep(img) {
    max-width: 100%;
    height: auto;
  }

  .help-btn {
    background: #e8f8f4;
    border: 1px solid #1b9a94;
    color: #1b6f6b;
    /* Values match the fixed sizes at >= 375px and scale down with the logo below that */
    min-height: 30px;
    padding: clamp(4px, 1.6vw, 6px) clamp(8px, 3.5vw, 13px);
    gap: clamp(4px, 2.1vw, 8px);
    border-radius: 999px;
    font-size: clamp(12px, 3.75vw, 14px);
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    line-height: 1.2;
    transition: background 0.2s ease, border-color 0.2s ease, transform 0.1s ease;
  }

  .help-btn__icon {
    flex: 0 0 auto;
    width: clamp(20px, 7.5vw, 28px);
    height: clamp(20px, 7.5vw, 28px);
  }

  .help-btn__label {
    white-space: nowrap;
  }

  .help-btn:hover {
    background: #d7f1ea;
    border-color: #178984;
  }

  .help-btn:active {
    transform: translateY(1px);
  }

  .iframe-container {
    position: fixed;
    bottom: 6rem;
    right: 0;
    width: 400px;
    height: 300px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
    z-index: 9999;
  }

  .iframe-container iframe {
    width: 100%;
    height: 100%;
    border: none;
  }

  .st0 {
    fill: #0e7275;
  }

  .st1 {
    fill: #00b2b8;
  }

  @media (min-width: 1280px) {
    .custom-width{
      max-width: 70%;
    }
  }

  @media (max-width: 1279px) {
    .custom-width {
      max-width: none;
    }
  }

  .custom-margin {
    padding-top: 9px !important;
    padding-bottom: 9px !important;
  }

  @media (max-width: 1199px) {
    .header--sticky-mobile {
      position: sticky;
      top: 0;
      z-index: 30;
    }
  }
</style>
